/**
 * ViewingContextBadge — ambient plane·role indicator (U4 Viewing Context)
 *
 * Structural badge only — no tutorial copy, no guidance text.
 * Reads plane from URL, role from JWT entityType.
 *
 * @see .cursor/plans/archived/2026-q2/indirect-ux-parallel/stream-u4-viewing-context.md
 */

import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { matchPathPlane } from '@shared/tenant-host';
import type { PathPlane } from '@shared/tenant-host';
import {
  planeAccentTokenV1,
  planeLabelV1,
} from '@shared/four-plane-product-ux-visual-consistency-and-accessibility';
import { getCurrentTokenPayload } from '../../utils/jwtUtils';
import type { SiteEntityType } from '@shared/tenant-host';
import { coerceExternalBusinessRole } from '@shared/types/external-entity-ontology';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * `Customer` / `Partner` / `ExternalParticipant` were added so `external`
 * entityType is never collapsed to a single guessed role (previously always
 * `Supplier`). `ExternalParticipant` is the fail-safe default for any
 * external actor whose specific relation role cannot be determined from the
 * JWT payload — it must never be silently upgraded to `Supplier`.
 */
export type ViewingRole =
  | 'Guest'
  | 'Member'
  | 'Supplier'
  | 'Customer'
  | 'Partner'
  | 'ExternalParticipant'
  | 'Internal'
  | 'Owner';

/** Fields already present on the JWT payload — no new identity/role authority. */
export interface ViewingRoleContext {
  role?: string | null;
  partnerTierId?: string | null;
}

export interface ViewingContext {
  plane: PathPlane;
  role: ViewingRole;
  isPreview: boolean;
}

// ---------------------------------------------------------------------------
// Label + color maps (SSOT: planeVisualToneV1 — existing semantic CSS tokens only)
// ---------------------------------------------------------------------------

const PLANE_LABELS: Record<PathPlane, string> = {
  public: planeLabelV1('public'),
  relationship: planeLabelV1('relationship'),
  business: planeLabelV1('business'),
  platform: planeLabelV1('platform'),
};

const PLANE_COLORS: Record<PathPlane, string> = {
  public: planeAccentTokenV1('public'),
  relationship: planeAccentTokenV1('relationship'),
  business: planeAccentTokenV1('business'),
  platform: planeAccentTokenV1('platform'),
};

// ---------------------------------------------------------------------------
// Logic helpers
// ---------------------------------------------------------------------------

/**
 * External viewing role — fail-safe, NEVER defaults to Supplier. Only
 * resolves Customer/Supplier/Partner when the JWT payload's `role` or
 * `partnerTierId` actually indicates it; anything else falls back to
 * `ExternalParticipant`.
 */
function resolveExternalViewingRole(context: ViewingRoleContext | undefined): ViewingRole {
  const role = context?.role ?? null;
  if (role === 'external_customer') return 'Customer';
  if (role === 'external_supplier') return 'Supplier';

  const coerced = role ? coerceExternalBusinessRole(role) : null;
  if (coerced === 'customer') return 'Customer';
  if (coerced === 'supplier') return 'Supplier';
  if (coerced === 'buyer') return 'Partner';

  if (context?.partnerTierId) return 'Partner';

  return 'ExternalParticipant';
}

export function resolveViewingRole(
  plane: PathPlane,
  entityType: SiteEntityType,
  context?: ViewingRoleContext
): ViewingRole {
  if (plane === 'platform') return 'Owner';
  if (!entityType) return 'Guest';
  if (entityType === 'external') return resolveExternalViewingRole(context);
  if (entityType === 'internal') return 'Internal';
  if (entityType === 'user') return 'Member';
  return 'Guest';
}

function detectPreviewMode(pathname: string, search: string): boolean {
  if (new URLSearchParams(search).has('preview')) return true;
  return pathname.startsWith('/system/site-studio');
}

export function resolveViewingContext(pathname: string, search: string): ViewingContext {
  const plane = matchPathPlane(pathname, 'tenant');
  const payload = getCurrentTokenPayload();
  const entityType = (payload?.entityType ?? null) as SiteEntityType;
  const role = resolveViewingRole(plane, entityType, {
    role: payload?.role ?? null,
    partnerTierId: payload?.partnerTierId ?? null,
  });
  const isPreview = detectPreviewMode(pathname, search);
  return { plane, role, isPreview };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ViewingContextBadgeProps {
  /** Override context — used in tests. If omitted, reads from URL + JWT. */
  contextOverride?: ViewingContext;
}

export const ViewingContextBadge: React.FC<ViewingContextBadgeProps> = ({ contextOverride }) => {
  const location = useLocation();

  const ctx = useMemo<ViewingContext>(
    () => contextOverride ?? resolveViewingContext(location.pathname, location.search),
    [contextOverride, location.pathname, location.search]
  );

  const dotColor = PLANE_COLORS[ctx.plane];
  const planeLabel = PLANE_LABELS[ctx.plane];

  return (
    <div
      className="vcb-root"
      data-testid="viewing-context-badge"
      data-plane={ctx.plane}
      data-role={ctx.role}
      data-preview={ctx.isPreview ? 'true' : 'false'}
      aria-label={`${planeLabel} plane — ${ctx.role} context${ctx.isPreview ? ' (preview)' : ''}`}
      role="status"
      aria-live="off"
      style={ctx.isPreview ? VCB_STYLES.rootPreview : VCB_STYLES.root}
    >
      {/* plane dot */}
      <span style={{ ...VCB_STYLES.dot, backgroundColor: dotColor }} aria-hidden="true" />

      {/* label */}
      <span style={VCB_STYLES.text}>
        {planeLabel}
        <span style={VCB_STYLES.separator} aria-hidden="true">
          ·
        </span>
        {ctx.role}
      </span>

      {ctx.isPreview && (
        <span style={VCB_STYLES.previewTag} aria-label="preview mode">
          Preview
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline styles (no CSS module dependency — badge is fully self-contained)
// ---------------------------------------------------------------------------

const VCB_BASE: React.CSSProperties = {
  position: 'fixed',
  bottom: 'var(--space-3, 12px)',
  left: 'var(--space-3, 12px)',
  zIndex: 9998,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1, 4px)',
  padding: '3px 8px',
  borderRadius: 'var(--radius-full, 9999px)',
  background: 'var(--overlay-scrim)',
  backdropFilter: 'blur(8px)',
  border: '1px solid var(--border-glass)',
  fontSize: 'var(--font-size-xs, 11px)',
  lineHeight: 1.4,
  fontWeight: 'var(--font-weight-medium, 600)' as React.CSSProperties['fontWeight'],
  letterSpacing: '0.04em',
  color: 'var(--color-text-soft, #b8c1e6)',
  opacity: 0.55,
  transition: 'opacity var(--motion-duration-normal) var(--motion-easing-default)',
  pointerEvents: 'none',
  userSelect: 'none',
};

const VCB_STYLES = {
  root: VCB_BASE,
  rootPreview: {
    ...VCB_BASE,
    border: '1px solid var(--color-gold, #ffd56b)',
    opacity: 0.85,
  } satisfies React.CSSProperties,
  dot: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  } satisfies React.CSSProperties,
  text: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  } satisfies React.CSSProperties,
  separator: {
    color: 'var(--color-text-muted, #7a86b8)',
  } satisfies React.CSSProperties,
  previewTag: {
    marginLeft: 4,
    padding: '0 5px',
    borderRadius: 'var(--radius-sm, 4px)',
    background: 'var(--glow-gold)',
    color: 'var(--color-gold, #ffd56b)',
    fontSize: '10px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  } satisfies React.CSSProperties,
} as const;

export default ViewingContextBadge;
